/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosEnumEdsGradients } from "@cosmos/hygraph-schema";

import { EdsIcon } from "@components/elements";
import { css, cx } from "@panda/css";
import { token } from "@panda/tokens";

import type { CSSProperties } from "react";
import type { OrionCardFragment } from "@cosmos/hygraph-schema";

interface CssVariables extends CSSProperties {
	"--orion-icon-gradient"?: string;
	"--orion-border-gradient"?: string;
}

/**
 * 64px tile: blue-300 hairline border, a low-opacity gradient fill, and a blurred copy of the
 * same gradient behind it for the soft halo in the Figma. Three stacked layers rather than one
 * background so the halo can blur and scale without dragging the border with it.
 *
 * `filter: blur` is an expensive composite — `will-change: transform` promotes the halo to its own
 * GPU layer (same treatment as the AccordionTierCard CTA asset glow).
 */
const iconTileStyles = css({
	position: "relative",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	flexShrink: "0",
	width: "64px",
	height: "64px",
	borderRadius: "eds.borderRadius.16",
	_before: {
		content: '""',
		position: "absolute",
		inset: 0,
		borderRadius: "18px",
		border: "1.15px solid transparent",
		backgroundOrigin: "border-box",
		background: "var(--orion-border-gradient)",
		mask: "linear-gradient(#000 0 0) padding-box,linear-gradient(#000 0 0)",
		maskComposite: "exclude",
	},
	"& .orion-card-icon__halo": {
		position: "absolute",
		inset: "0",
		borderRadius: "inherit",
		background: "var(--orion-icon-gradient)",
		filter: "blur(3px)",
		opacity: "0.1",
		transform: "scale(1.1)",
		pointerEvents: "none",
		willChange: "transform",
		zIndex: "0",
	},
	"& .orion-card-icon__fill": {
		position: "absolute",
		inset: "0",
		borderRadius: "inherit",
		background: "var(--orion-icon-gradient)",
		opacity: "0.15",
		pointerEvents: "none",
		zIndex: "1",
		mask: "radial-gradient(closest-side, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.7) 60%, rgba(0, 0, 0, 0) 120%)",
	},
	"& .orion-card-icon__glyph": {
		position: "relative",
		zIndex: "2",
		//height: "32px !important",
		//width: "32px !important",
	},
});

export interface OrionCardIconProps {
	icon?: OrionCardFragment["icon"];
	gradient?: OrionCardFragment["iconGradient"];
	className?: string;
}

export const OrionCardIcon = ({ icon, gradient, className }: OrionCardIconProps): React.JSX.Element | null => {
	if (!icon) return null;

	// The tile chrome is gradient-driven, so it needs a concrete value even when the card leaves
	// `iconGradient` unset. Blue matches the Figma default and the blue-300 border.
	const resolvedGradient = gradient || CosmosEnumEdsGradients.Blue;
	const style: CssVariables = {
		"--orion-icon-gradient": token(`gradients.eds.gradient.${resolvedGradient}`),
		"--orion-border-gradient": `linear-gradient(225deg, ${token(`colors.eds.palette.${resolvedGradient}.500`)}, rgba(255,255,255,0), ${token(`colors.eds.palette.${resolvedGradient}.300`)})`,
	};

	return (
		<div className={cx("orion-card-icon", iconTileStyles, className)} style={style}>
			<span className="orion-card-icon__halo" aria-hidden="true" />
			<span className="orion-card-icon__fill" aria-hidden="true" />
			{/* EdsIcon already handles the gradient glyph fill via its `gradient` prop. */}
			<EdsIcon icon={icon} size="lg" gradient={resolvedGradient} className="orion-card-icon__glyph" />
		</div>
	);
};
